'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import type { Room } from 'livekit-client';
import {
  isVideoTrack,
  LocalVideoTrack,
  Track,
  VideoQuality,
} from 'livekit-client';
import toast from 'react-hot-toast';
import {
  getEffectiveVideoQualityProfile,
  getVideoCaptureMediaConstraints,
  readVideoQualityProfile,
} from '@/lib/video-quality-profile';
import {
  getEffectiveScreenShareQualityProfile,
  getScreenShareMediaConstraints,
  readScreenShareQualityProfile,
} from '@/lib/screen-share-profile';
import {
  applyIncomingVideoQualitySkippingScreenShare,
  shouldClearVirtualBackgroundOnEconomy,
} from '@/lib/screen-share-quality-guard';
import {
  capLocalCameraForPerformance,
  restoreLocalCameraPublishBitrate,
} from '@/lib/camera-publish-bitrate';
import { ECONOMY_MODE_STORAGE_KEY, readEconomyModeEnabled } from '@/lib/economy-mode-storage';
import { useLayoutView } from './LayoutContext';
import { useNoiseCancellation } from './NoiseCancellationContext';

const STORAGE_KEY = ECONOMY_MODE_STORAGE_KEY;
export const ECONOMY_MODE_CHANGED_EVENT = 'evolumeet:economy-mode-changed';

export { readEconomyModeEnabled };

export function writeEconomyModeEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  window.dispatchEvent(
    new CustomEvent(ECONOMY_MODE_CHANGED_EVENT, { detail: { enabled } }),
  );
}

type EconomyModeContextValue = {
  economyModeEnabled: boolean;
  setEconomyModeEnabled: (enabled: boolean) => void;
  toggleEconomyMode: () => void;
};

const EconomyModeContext = React.createContext<EconomyModeContextValue | null>(null);

export function EconomyModeProvider({ children }: { children: React.ReactNode }) {
  const [economyModeEnabled, setEnabledState] = React.useState(false);

  React.useEffect(() => {
    setEnabledState(readEconomyModeEnabled());
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      setEnabledState(detail?.enabled ?? readEconomyModeEnabled());
    };
    window.addEventListener(ECONOMY_MODE_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(ECONOMY_MODE_CHANGED_EVENT, onChange);
  }, []);

  const setEconomyModeEnabled = React.useCallback((enabled: boolean) => {
    writeEconomyModeEnabled(enabled);
    setEnabledState(enabled);
    toast(
      enabled
        ? 'Modo economia: câmera e tela reduzidos — efeitos desligados para melhor fluidez.'
        : 'Modo economia desativado.',
      { icon: enabled ? '⚡' : 'ℹ️', duration: 4500 },
    );
  }, []);

  const toggleEconomyMode = React.useCallback(() => {
    setEconomyModeEnabled(!readEconomyModeEnabled());
  }, [setEconomyModeEnabled]);

  const value = React.useMemo(
    () => ({ economyModeEnabled, setEconomyModeEnabled, toggleEconomyMode }),
    [economyModeEnabled, setEconomyModeEnabled, toggleEconomyMode],
  );

  return (
    <EconomyModeContext.Provider value={value}>{children}</EconomyModeContext.Provider>
  );
}

export function useEconomyMode(): EconomyModeContextValue {
  const ctx = React.useContext(EconomyModeContext);
  if (!ctx) {
    throw new Error('useEconomyMode must be used within EconomyModeProvider');
  }
  return ctx;
}

export function useOptionalEconomyMode(): EconomyModeContextValue | null {
  return React.useContext(EconomyModeContext);
}

function getLocalMediaStreamTrack(
  room: Room,
  source: Track.Source,
): MediaStreamTrack | undefined {
  const pub = room.localParticipant.getTrackPublication(source);
  const t = pub?.track as { mediaStreamTrack?: MediaStreamTrack } | undefined;
  const ms = t?.mediaStreamTrack;
  return ms && ms.readyState === 'live' ? ms : undefined;
}

/** Aplica constraints de economia também à tela (exportado para testes). */
export async function applyEconomyScreenShareConstraints(
  room: Room,
  active: boolean,
): Promise<void> {
  const msTrack = getLocalMediaStreamTrack(room, Track.Source.ScreenShare);
  if (!msTrack) return;

  try {
    if (active) {
      await msTrack.applyConstraints(getScreenShareMediaConstraints('normal', 'economy'));
      return;
    }
    const profile = getEffectiveScreenShareQualityProfile(
      readScreenShareQualityProfile(),
      false,
    );
    await msTrack.applyConstraints(getScreenShareMediaConstraints(profile, 'default'));
  } catch (error) {
    console.warn('[economyMode] screenShare applyConstraints:', error);
  }
}

async function applyEconomyCaptureProfile(room: Room, active: boolean): Promise<void> {
  const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const track = publication?.track;
  if (!track || !isVideoTrack(track)) return;

  const localTrack = track as LocalVideoTrack;
  const mediaTrack = localTrack.mediaStreamTrack;
  if (!mediaTrack) return;

  try {
    if (active) {
      // Cap de bitrate/fps com TODAS as camadas ativas (prioritizePerformance()
      // desligava camadas e o SFU pintava o indicador de vermelho).
      await capLocalCameraForPerformance(room);
      await localTrack.stopProcessor().catch(() => {});
      await mediaTrack.applyConstraints({
        width: { max: 854, ideal: 640 },
        height: { max: 480, ideal: 360 },
        frameRate: { max: 20 },
      });
      return;
    }

    const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), false);
    await mediaTrack.applyConstraints(getVideoCaptureMediaConstraints(effective));
    await restoreLocalCameraPublishBitrate(room);
  } catch (error) {
    console.warn('[economyMode] applyConstraints:', error);
  }
}

export type EconomyModeRoomEffectsOptions = {
  setMaxTilesFromUser: (n: number) => void;
  setNoiseFilterEnabled?: (enabled: boolean) => void;
  clearVirtualBackground?: () => void;
};

/** Aplica limites de performance quando modo economia está ativo na sala. */
export function useEconomyModeRoomEffects(
  room: Room | undefined,
  options: EconomyModeRoomEffectsOptions,
) {
  const { economyModeEnabled } = useEconomyMode();
  const wasEnabledRef = React.useRef(false);

  React.useEffect(() => {
    if (!room || !economyModeEnabled) {
      // Ao desligar economia: devolve captura/bitrate da câmera e da tela ao perfil.
      if (room && wasEnabledRef.current) {
        wasEnabledRef.current = false;
        void applyEconomyCaptureProfile(room, false);
        void applyEconomyScreenShareConstraints(room, false);
      }
      return;
    }
    wasEnabledRef.current = true;

    options.setMaxTilesFromUser(4);
    options.setNoiseFilterEnabled?.(false);
    if (shouldClearVirtualBackgroundOnEconomy()) {
      options.clearVirtualBackground?.();
    }

    void applyEconomyCaptureProfile(room, true);
    void applyEconomyScreenShareConstraints(room, true);
    applyIncomingVideoQualitySkippingScreenShare(room, VideoQuality.LOW);
  }, [
    room,
    economyModeEnabled,
    options.setMaxTilesFromUser,
    options.setNoiseFilterEnabled,
    options.clearVirtualBackground,
  ]);
}

/** Ponte dentro da sala — aplica efeitos quando modo economia está ativo. */
export function EconomyModeRoomBridge({
  onClearVirtualBackground,
}: {
  onClearVirtualBackground: () => void;
}) {
  const room = useRoomContext();
  const { setMaxTilesFromUser } = useLayoutView();
  const { setEnabled: setNoiseFilterEnabled } = useNoiseCancellation();

  useEconomyModeRoomEffects(room, {
    setMaxTilesFromUser,
    setNoiseFilterEnabled,
    clearVirtualBackground: onClearVirtualBackground,
  });

  return null;
}
